import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Listbox, ListboxItem, Chip, ScrollShadow, Avatar, Skeleton } from "@heroui/react";
import { Key } from '@react-types/shared';

import { RoleContext } from '@/app/api/common';
import { store, useAppSelector } from '@/app/store';
import { FirmwareDescriptor } from '@/features/projects/projectsSlice';
import { ChannelList, ChannelListDescriptor, ChannelListManager, checkNoError, copyChannelList, copyTagDescriptors, getChannelListByEpgId, getField, getFieldActivation, getFieldExpiry, setField, setFieldActivation, setFieldExpiry, UrlInfo } from '@/features/channels/channelsSlice';
import { copyProjectInfo, copyProjectLimits, HotelInfo, HotelLimits, LanguageSelectorConfig } from '@/features/projects/projectsSlice';
import {
  getMwLight,
  mwLightCandidates,
  mwLightEntryLabel,
  mwLightOverlaps,
  mwLightRivalTooltip,
  needsNoActiveMwLightUserWarning,
  noActiveMwLightUserWarning,
} from '@/features/projects/mwLight';
import { canOverrideScreentime } from '@/features/screentime/screenTimeApi';
import { languageSelectorAclKey } from '@/features/channels/channelsSlice';

import { checkoutSwitchedOn, hotelCheckoutState } from '@/features/master/masterSlice';
import { getProjectGroupById } from '@/features/projectGroups/projectGroupsSlice';

import Button from '@/components/Common/Button';
import Dropdown, { FieldInfo } from '@/components/Common/Dropdown';
//import { NumberText } from '@/components/Common/Field';
import FlexRow from '@/components/Common/FlexRow';
import Input, { TextArea } from '@/components/Common/Input'
import UrlsEditor, { expiryToLocalDateTime, formatExpiryDate, getExpiryCountdown, localDateTimeToExpiry } from '@/components/Common/UrlsEditor';

import { CatHeader } from './Project';
import LanguageSelectorSettings from './LanguageSelectorSettings';
import ProjectLists from './ProjectLists';
import ScreenTimeSettings from './ScreenTimeSettings';
import FirmwaresEditor from '@/components/Common/FirmwaresEditor';

function generatePass(length: number, alpha: boolean) {
    let result = '';
    const characters =	alpha
    					?	'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    					:	'0123456789';
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
      counter += 1;
    }
    return result;
}

// The backend validates the public IP for format and for global uniqueness on
// save, so this is a courtesy check that names the problem before the round
// trip - not the authority. IPv6 is recognised loosely (hex groups and colons)
// because the backend accepts whatever net.ParseIP does, and flagging a valid
// v6 address as broken would be worse than letting the server have the last
// word.
const IPV4_RE = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
const IPV6_CHARS_RE = /^[0-9A-Fa-f:]+$/;

// Loose enough to accept anything net.ParseIP would, strict enough that the
// "Valid" pill is not handed out to a run of colons: at most one "::", and
// either compressed or all eight groups written out.
function isIpv6(ip: string): boolean {
  if (ip.indexOf(':') < 0 || !IPV6_CHARS_RE.test(ip))
    return false;

  if (ip.indexOf(':::') > -1)
    return false;

  const halves = ip.split('::');
  if (halves.length > 2)
    return false;

  const groups = ip.split(':').filter((g) => g !== '');
  if (groups.length > 8 || groups.some((g) => g.length > 4))
    return false;

  return halves.length === 2 || groups.length === 8;
}

type IpKind = 'empty' | 'valid' | 'invalid';

function classifyIp(value: string): IpKind {
  const ip = value.trim();
  if (ip === '')
    return 'empty';
  return IPV4_RE.test(ip) || isIpv6(ip) ? 'valid' : 'invalid';
}

const ipPillBase =	'inline-flex items-center gap-[6px] rounded-full px-[10px] py-[4px] ' +
					'text-[13px] font-medium whitespace-nowrap shrink-0';
const ipPillGood = ipPillBase + ' bg-[#E8F5EC] text-[#2E7D4F]';
const ipPillBad = ipPillBase + ' bg-[#FDECEA] text-[#B3261E]';

// ---- Admin cards (Checkout, Expiration) -------------------------------------
// The same shell as the Language choices card: an icon tile, a title with its
// subtitle, and a pill on the right that says what applies right now.

type PillTone = "green" | "grey" | "red" | "amber" | "blue";

const pillTones: { [tone in PillTone]: [string, string, string] } = {
  // [background, dot, text]
  green:	['bg-[#DCFCE7]', 'bg-[#16A34A]', '!text-[#166534]'],
  grey:		['bg-[#F1F3F5]', 'bg-[#9AA1AB]', '!text-[#4B5563]'],
  red:		['bg-[#FDECEA]', 'bg-[#B3261E]', '!text-[#B3261E]'],
  amber:	['bg-[#FEF3C7]', 'bg-[#D97706]', '!text-[#92400E]'],
  blue:		['bg-[#E8F0F8]', 'bg-[#1B69AA]', '!text-[#1B69AA]'],
};

const StatusPill = (props: { tone: PillTone, children?: React.ReactNode }) => {
  const [bg, dot, text] = pillTones[props.tone];

  return (
  <FlexRow align="center"
           flex={0}
           className={'gap-[6px] shrink-0 rounded-full px-[10px] py-[4px] whitespace-nowrap ' + bg}>
    <span className={'w-[6px] h-[6px] rounded-full shrink-0 ' + dot} />
    <span className={'text-[12px] font-medium leading-[16px] ' + text}>
      { props.children }
    </span>
  </FlexRow>
  );
};

const Hint = (props: { children?: React.ReactNode }) => (
  <span className="text-[--Dark-Grey] text-[12px] leading-[16px]">
    { props.children }
  </span>
);

interface SettingsCardProps {
  icon:			string;
  title:		string;
  subtitle:		string;
  pill?:		React.ReactNode;
  children?:	React.ReactNode;
}

const SettingsCard = (props: SettingsCardProps) => (
  <FlexRow direction="column"
           align="start"
           className="w-full max-w-[448px] mt-[16px] rounded-[12px] border-solid border-1 border-[--Lines] bg-white shadow-sm overflow-hidden">
    <FlexRow align="center"
             className="w-full gap-[12px] px-[16px] py-[12px] border-solid border-0 border-b-1 border-[--Lines]">
      <div className="flex items-center justify-center shrink-0 w-[36px] h-[36px] rounded-[8px] bg-[--primetime-30]">
        <span className="material-icons !text-[--Primetime-main] !text-[18px]">
          { props.icon }
        </span>
      </div>
      <FlexRow direction="column"
               align="start"
               className="gap-[2px] min-w-0">
        <span className="b14 !text-black">
          { props.title }
        </span>
        <Hint>
          { props.subtitle }
        </Hint>
      </FlexRow>
      <FlexRow justify="end"
               flex={0}
               className="shrink-0 ml-auto">
        { props.pill }
      </FlexRow>
    </FlexRow>
    <FlexRow direction="column"
             align="start"
             className="w-full gap-[12px] px-[16px] py-[16px]">
      { props.children }
    </FlexRow>
  </FlexRow>
);

interface SegmentedOption {
  label:		string;
  sub:			string;
}

// Two (or more) side-by-side choices on a grey track; the chosen one is a
// white raised tile.
const SegmentedSwitch = (props: { options: SegmentedOption[], selected: number, onSelect: (i: number) => void }) => (
  <FlexRow align="center"
           className="w-full gap-[4px] p-[4px] rounded-[8px] bg-[#EEF1F5]">
  {props.options.map((opt, i) =>
    <button key={i}
            type="button"
            aria-pressed={i === props.selected}
            className={[
              'flex-1 min-w-0 flex flex-col items-center gap-[2px] rounded-[6px] px-[8px] py-[8px] border-0',
              i === props.selected
              ?	'bg-white shadow-sm'
              :	'bg-transparent hover:bg-white/60',
            ].join(' ')}
            onClick={() => props.onSelect(i)}>
      <span className={'text-[12px] font-semibold leading-[16px] ' + (i === props.selected ? 'text-black' : 'text-[--Dark-Grey]')}>
        { opt.label }
      </span>
      <span className={'text-[10px] leading-[14px] ' + (i === props.selected ? 'text-[--Primetime-main]' : 'text-[--Dark-Grey]')}>
        { opt.sub }
      </span>
    </button>
  )}
  </FlexRow>
);

// Quick picks for the expiration date, in Unix seconds at a whole minute (the
// date-time input has no seconds, so the field shows exactly what is saved).
function expiryPresets(): { key: string, ts: number }[] {
  const now = new Date();
  now.setSeconds(0, 0);
  const plusDays = (days: number) => Math.floor(now.getTime() / 1000) + days * 86400;
  const endOfYear = new Date(now.getFullYear(), 11, 31, 23, 59, 0, 0);

  return [
    { key: 'in_30',			ts: plusDays(30) },
    { key: 'in_90',			ts: plusDays(90) },
    { key: 'end_of_year',	ts: Math.floor(endOfYear.getTime() / 1000) },
  ];
}

export interface ProjectSettingsProps {
  project:				HotelInfo;
  setProject:			(val: HotelInfo) => any;
  limits:				HotelLimits | null;
  setLimits:			(lim: HotelLimits) => any;
  idInvalid:			boolean;
  nameInvalid:			boolean;
  //managerInvalid:		boolean;
  //partnerInvalid:		boolean;
  setUrlInvalid:		(val: boolean) => any;
  triedSave:			boolean;
}


const ProjectSettings = (props: ProjectSettingsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const logins = useAppSelector((state) => state.logins);
  const role = React.useContext(RoleContext);
  const globalLists = useAppSelector((state) => state.channelLists);
  const hotelChannelLists = useAppSelector((state) => state.hotelChannelLists);
  const tags=useAppSelector((state)=>state.tags.tags)
  const languages = useAppSelector((state) => state.languages);
  const trackFormats = useAppSelector((state) => state.trackFormats);
  const projectGroups = useAppSelector((state) => state.projectGroups.groups);
  const [combinedLists, setCombinedLists] = React.useState({lists: null} as ChannelListManager);
  const [partnerOpts, setPartnerOpts] = React.useState([] as FieldInfo[]);
  const [tagOpts, setTagOpts] = React.useState([] as FieldInfo[]);
  const [languageOpts, setLanguageOpts] = React.useState([] as FieldInfo[]);
  const [trackFormatOpts, setTrackFormatOpts] = React.useState([] as FieldInfo[]);
  //const [selectedAdded, setSelectedAdded] = React.useState(new Set<Key>());
  //const [selectedAvailable, setSelectedAvailable] = React.useState(new Set<Key>());
  
  React.useEffect(() => {
    if (!globalLists.lists) {
      store.getChannelLists();
    }

    if (props.project) {
      store.getChannelLists(props.project.id, false);
    }
  }, []);

  
  React.useEffect(() => {
    if (	props.project &&
        	!hotelChannelLists.lists
       ) {
      store.getChannelLists(props.project.id, false);
    }
  }, [props.project]);
  
  React.useEffect(() => {
    if (	!props.project ||
    		!props.limits ||
        	props.limits.id != props.project.id ||
        	props.limits.activation_code != ""
       )
      return;
    
    const newCode = generatePass(4, false);
    console.log(
      "ProjectSettings/useEffect/limits" +
      "/limits.id=" + props.limits.id +
      "/limits.activation_code=" + props.limits.activation_code +
      "/newCode=" + newCode
    );
    updateActivationCode(newCode);
  }, [props.limits, props.project]);
  
  React.useEffect(() => {
    const pOpts: FieldInfo[] = [];
    let currentUserFound = false;
    for (const desc of logins.logins.descriptors) {
      pOpts.push({
        value:			desc.username,
        desc:			`${desc.username} (${desc.role})`,
      });
      if (desc.username === logins.login?.username) {
        currentUserFound = true;
      }
    }

    if (!currentUserFound && logins.login) {
      pOpts.push({
        value:			logins.login.username,
        desc:			`${logins.login.username}`,
      });
    }

    setPartnerOpts(pOpts);
  }, [logins.logins.descriptors, logins.login]);
  
  React.useEffect(() => {
    if (role.admin || role?.privilages?.includes("ViewTags")) {
      store.getTags();
    }
    if (role.admin || role?.privilages?.includes("ViewFirmware")) {
      store.getFirmwares();
    }
    if (role.admin || role?.privilages?.includes("ViewTrackFormats") || role?.privilages?.includes("SeeAllProjects")) {
      store.getTrackFormats();
    }
    store.getLanguages();
  }, [role.admin, role?.privilages]);

  // Task-80 §4.3 — the Project group dropdown's options. Everyone may read the
  // list; it holds only the groups the caller can see.
  React.useEffect(() => {
    store.getProjectGroups();
  }, []);

  React.useEffect(() => {
    const tOpts: FieldInfo[] = tags?.map((tag: any) => ({
      value: tag.tag,
      desc: tag.tag,
    }));
    setTagOpts(tOpts);
  }, [tags]);

  React.useEffect(() => {
    const lOpts: FieldInfo[] = [
      { value: "default", desc: "Default" },
      ...(languages?.languages?.map((lang: any) => ({
        value: lang.code,
        desc: lang.name.charAt(0).toUpperCase() + lang.name.slice(1),
      })) || []),
    ];
    setLanguageOpts(lOpts);
  }, [languages.languages]);

  React.useEffect(() => {
    const tfOpts: FieldInfo[] = [
      { value: "", desc: "Default" },
      ...(trackFormats?.track_formats?.map((tf: any) => ({
        value: tf.key,
        desc: tf.key,
      })) || []),
    ];
    setTrackFormatOpts(tfOpts);
  }, [trackFormats.track_formats]);

  // Handler for firmware selection changes
  const handleFirmwareChange = React.useCallback((firmware: FirmwareDescriptor[]) => {
    console.log("ProjectSettings/handleFirmwareChange/firmware=", firmware);
    if (!props.limits)
      return;

    let limits = copyProjectLimits(props.limits);
    if (!limits.overrides) {
      limits.overrides = {};
    }

    limits.overrides.firmware = firmware;
    console.log("ProjectSettings/handleFirmwareChange/limits.overrides=", limits.overrides);
    props.setLimits(limits);
  }, [props.limits, props.setLimits]);

  // The panel's own Done button only closes it (see LanguageSelectorSettings)
  // — every edit already lands here on props.limits, and it is the project's
  // own Save button that persists it.
  const updateLanguageSelector = React.useCallback((cfg: LanguageSelectorConfig) => {
    if (!props.limits)
      return;

    let limits = copyProjectLimits(props.limits);
    limits.language_selector = cfg;
    props.setLimits(limits);
  }, [props.limits, props.setLimits]);
  
  React.useEffect(() => {
    const combined: ChannelListManager = {
      lists: [],
    };

    if (!combined.lists)
      return;

    for (const list of hotelChannelLists.lists) {
      combined.lists.push(copyChannelList(list));
    }

    if (globalLists.lists) {
      for (const list of globalLists.lists) {
        combined.lists.push(copyChannelList(list));
      }
    }

    setCombinedLists(combined);
  }, [globalLists.lists, hotelChannelLists.lists]);
  
  const updatePartner = React.useCallback((val: string[]) => {
    if (!props.limits)
      return;
    
    let limits = copyProjectLimits(props.limits);
    
    limits.executives.partner = val;
    props.setLimits(limits);
  }, [props.limits]);

  const updateTags = (val: string[]) => {
    let proj = copyProjectInfo(props.project);
    proj.tags = val.map((tag) => ({ tag }));
    props.setProject(proj);
  };

  const updateLanguage = (val: string) => {
    let proj = copyProjectInfo(props.project);
    if (val === "default") {
      proj.languages = [];
    } else {
      const lang = languages?.languages?.find((l: any) => l.code === val);
      proj.languages = lang
        ? [{ code: lang.code, priority: lang.priority }]
        : [];
    }
    props.setProject(proj);
  };
  
  const updateManager = React.useCallback((val: string[]) => {
    if (!props.limits)
      return;
    
    let limits = copyProjectLimits(props.limits);
    
    limits.executives.manager = val;
    props.setLimits(limits);
  }, [props.limits]);
  
  const updateChainManager = React.useCallback((val: string[]) => {
    if (!props.limits)
      return;

    let limits = copyProjectLimits(props.limits);

    limits.executives.chain_manager = val;
    props.setLimits(limits);
  }, [props.limits]);

  // ---- MW light (ticket #91, §2.1) -----------------------------------------

  const loginDescriptors = logins.logins.descriptors;

  const mwLightNames = React.useMemo(
    () => getMwLight(props.limits ? props.limits.executives : null),
    [props.limits]
  );

  // Setting it writes the key, so a save from this page is always explicit:
  // [] clears the server's list, which is what an admin emptying the box means.
  const updateMwLight = React.useCallback((val: string[]) => {
    if (!props.limits)
      return;

    let limits = copyProjectLimits(props.limits);

    limits.executives.mw_light = val;
    props.setLimits(limits);
  }, [props.limits, props.setLimits]);

  // The options the server would accept, plus every name already stored (a
  // deleted or no-longer-eligible one has to stay offerable or it could never
  // be removed). Group chain managers are not fetched here — the server's own
  // sentence explains that one.
  const mwLightOpts = React.useMemo(() => {
    const names = mwLightCandidates({
      descriptors:	loginDescriptors || [],
      executives:	props.limits ? props.limits.executives : null,
      owner:		props.project.owner,
      selected:		mwLightNames,
    });

    return names.map((username) => ({
      value:	username,
      desc:		mwLightEntryLabel(username, loginDescriptors),
    })) as FieldInfo[];
  }, [loginDescriptors, props.limits, props.project.owner, mwLightNames]);

  // The other three lists grey out a name that is in MW light, and say why.
  const execOpts = React.useMemo(() => {
    if (mwLightNames.length === 0)
      return partnerOpts;

    return partnerOpts.map((opt) =>
      mwLightNames.indexOf(opt.value) > -1
      ?	{ ...opt, tooltip: mwLightRivalTooltip }
      :	opt
    );
  }, [partnerOpts, mwLightNames]);

  const mwLightOverlapList = React.useMemo(
    () => mwLightOverlaps(props.limits ? props.limits.executives : null),
    [props.limits]
  );

  const mwLightNoActive = React.useMemo(
    () => needsNoActiveMwLightUserWarning(
            props.limits ? props.limits.executives : null,
            loginDescriptors
          ),
    [props.limits, loginDescriptors]
  );

  // ---- The hotel's Checkout (ticket #91, §0.8 / §3.1a) ---------------------
  // Works like a room setting against its hotel's: undefined follows System
  // settings, true / false win over it. Admins only; Project.tsx loads
  // /master.json for them.

  const master = useAppSelector((state) => state.master);

  const hotelCheckout = React.useMemo(
    () => hotelCheckoutState(
            props.limits ? props.limits.checkout_enabled : undefined,
            checkoutSwitchedOn(master)
          ),
    [props.limits, master]
  );

  // What applies to this hotel right now: its own value when it differs from
  // System settings, otherwise System settings.
  const checkoutEffective =	hotelCheckout.differs
  							?	!checkoutSwitchedOn(master)
  							:	checkoutSwitchedOn(master);

  // Writes the value the button names. Sending the System settings value
  // makes the server remove the hotel's own value (§4.3).
  const pressHotelCheckout = React.useCallback(() => {
    if (!props.limits)
      return;

    let limits = copyProjectLimits(props.limits);

    limits.checkout_enabled = hotelCheckout.sets;
    props.setLimits(limits);
  }, [props.limits, props.setLimits, hotelCheckout]);

  // ---- Project expiration (ticket #93, §2.1) --------------------------------
  // Admins only. The editable value lives on props.limits; what is SAVED, and
  // the server's read-only `expired`, come from the project read as stored
  // (copyProjectInfoPublic does not carry `expired`).

  const storedProject = useAppSelector((state) => state.projects.active);

  const expiresAt = props.limits?.expires_at || 0;
  const expiredMessage = props.limits?.expired_message ?? "";
  const savedExpiresAt =	storedProject && storedProject.id === props.project.id
  							?	storedProject.limits?.expires_at || 0
  							:	0;
  const savedExpired =	!!(	storedProject &&
  							storedProject.id === props.project.id &&
  							storedProject.expired
  						  );

  // §3.4 — nothing tells the console when the time passes, so tick while a
  // future date is on screen and flip the status line locally at 0.
  const [nowSec, setNowSec] = React.useState(() => Math.floor(Date.now() / 1000));

  React.useEffect(() => {
    setNowSec(Math.floor(Date.now() / 1000));
    if (	!role.admin ||
    		expiresAt <= Math.floor(Date.now() / 1000)
       )
      return;

    const timer = setInterval(() => setNowSec(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(timer);
  }, [role.admin, expiresAt]);

  // The saved date counts as expired when the server says so (its clock is
  // the authority) or when our own countdown reached 0. An unsaved past date
  // has locked nothing yet - it gets the warning instead.
  const expiryUnsaved = expiresAt !== savedExpiresAt;
  const expiryExpired =	expiresAt > 0 &&
  						!expiryUnsaved &&
  						(savedExpired || expiresAt <= nowSec);
  const expiryPastWarning =	expiresAt > 0 &&
  							expiryUnsaved &&
  							expiresAt <= nowSec;

  const updateExpiresAt = (ts: number) => {
    if (!props.limits)
      return;

    const limits = copyProjectLimits(props.limits);
    limits.expires_at = ts;
    props.setLimits(limits);
  };

  const updateExpiredMessage = (val: string) => {
    if (!props.limits)
      return;

    const limits = copyProjectLimits(props.limits);
    limits.expired_message = val;
    props.setLimits(limits);
  };

  //console.log("ProjectSettings/props=", props);
  
  const bexioUrl = getField(props.project.urls, "bexio_url");
  const notes = getField(props.project.urls, "notes");
  const tracksOverride =	props.limits &&
  							props.limits.overrides &&
  							props.limits.overrides.tracks
  							?	props.limits.overrides.tracks
  							:	"";
  
  const isNew = props.project.is_new;
  
  // ---- Project group (Task-80 §4.3 / §4.4) ----------------------------------

  // "" is the explicit "No group" — the only place that ever sends it (§7).
  const noGroupKey = "__no_group__";

  const groupOpts = React.useMemo(() => {
    const opts: FieldInfo[] = [{
      value:	noGroupKey,
      desc:		t('projects:groups:no_group'),
    }];

    for (const group of projectGroups)
      opts.push({ value: group.id, desc: group.name });

    // A group that is not in the list (deleted meanwhile, or not visible)
    // still has to show as the current value.
    if (	props.project.group &&
        	!getProjectGroupById(props.project.group, projectGroups)
       )
      opts.push({ value: props.project.group, desc: props.project.group });

    return opts;
  }, [projectGroups, props.project.group, t]);

  const updateGroup = (val: string) => {
    let proj = copyProjectInfo(props.project);

    proj.group =	!val || val === noGroupKey
    				?	""
    				:	val;
    props.setProject(proj);
  };

  // What the project inherits, as STORED: the single read's group_settings,
  // else the list entry's. Never the dropdown's unsaved value.
  const storedGroupProject = useAppSelector((state) => state.projects.active);
  const storedGroupId =	storedGroupProject && storedGroupProject.id === props.project.id
  						?	storedGroupProject.group
  						:	undefined;
  const inheritedSettings =	storedGroupProject && storedGroupProject.id === props.project.id
  							?	storedGroupProject.group_settings
  							:	props.project.stats?.group_settings;
  const inheritedGroup = getProjectGroupById(storedGroupId, projectGroups);
  const inheritedChainManagers = inheritedSettings?.chain_manager || [];

  const updateId = (val: string) => {
    let proj = copyProjectInfo(props.project);
    
    proj.id = val;
    props.setProject(proj);
  };
  
  const updateName = (val: string) => {
    let proj = copyProjectInfo(props.project);
    
    proj.name = val;
    props.setProject(proj);
  };
  
  const updateUrl = (field: string, val: string) => {
    let proj = copyProjectInfo(props.project);
    
    proj.urls = setField(proj.urls, field, val);
    props.setProject(proj);
  };
  
  
  const updateActivationCode = (val: string) => {
    if (!props.limits)
      return;
    
    let limits = copyProjectLimits(props.limits);
    
    limits.activation_code = val;
    props.setLimits(limits);
  };
  
  const updateTracksOverride = (val: string) => {
    if (!props.limits)
      return;
    
    let limits = copyProjectLimits(props.limits);
    
    if (limits.overrides)
      limits.overrides.tracks = val;
    props.setLimits(limits);
  };
  
  const updateRooms = (val: string) => {
    if (!props.limits)
      return;
    
    let limits = copyProjectLimits(props.limits);
    
    limits.rooms = parseInt(val);
    props.setLimits(limits);
  };
  
  // public_ip is stored as a limits.urls entry (type "public_ip"), but is now
  // surfaced as a dedicated field below the room limit instead of inside the
  // generic URLs editor.
  const publicIp = getField(props.limits?.urls ?? [], "public_ip");

  const updatePublicIp = (val: string) => {
    if (!props.limits)
      return;

    let limits = copyProjectLimits(props.limits);
    limits.urls = setField(limits.urls ?? [], "public_ip", val.trim());
    props.setLimits(limits);
  };

  // The Easy Activation shortcut can be scheduled: activates_at is the "from"
  // end of the window, expires_at the "to" end, both unix seconds on the same
  // public_ip entry. 0 on either end means unbounded on that side, so an entry
  // with neither set behaves exactly as it did before the window existed.
  const publicIpActivatesAt = getFieldActivation(props.limits?.urls ?? [], "public_ip");
  const publicIpExpiresAt = getFieldExpiry(props.limits?.urls ?? [], "public_ip");
  const publicIpWindowInvalid =	publicIpActivatesAt > 0 &&
  								publicIpExpiresAt > 0 &&
  								publicIpActivatesAt >= publicIpExpiresAt;

  const publicIpKind = classifyIp(publicIp);

  const updatePublicIpActivatesAt = (val: string) => {
    if (!props.limits)
      return;

    let limits = copyProjectLimits(props.limits);
    limits.urls = setFieldActivation(limits.urls ?? [], "public_ip", localDateTimeToExpiry(val));
    props.setLimits(limits);
  };

  const updatePublicIpExpiresAt = (val: string) => {
    if (!props.limits)
      return;

    let limits = copyProjectLimits(props.limits);
    limits.urls = setFieldExpiry(limits.urls ?? [], "public_ip", localDateTimeToExpiry(val));
    props.setLimits(limits);
  };

  // The generic URLs editor reports its own validity; the public IP window is
  // outside that editor, so combine the two and let either one block the save.
  const [genericUrlsInvalid, setGenericUrlsInvalid] = React.useState(false);

  React.useEffect(() => {
    props.setUrlInvalid(genericUrlsInvalid || publicIpWindowInvalid || publicIpKind === 'invalid');
  }, [genericUrlsInvalid, publicIpWindowInvalid, publicIpKind, props.setUrlInvalid]);

  const updateLimitsUrls = (urls: UrlInfo[]) => {
    if (!props.limits)
      return;

    let limits = copyProjectLimits(props.limits);
    // The generic editor manages every URL EXCEPT public_ip (which has its own
    // field); preserve the existing public_ip entry so an edit here doesn't
    // drop it.
    const preserved = (props.limits.urls ?? []).filter((u) => u.type === "public_ip");
    limits.urls = [...urls, ...preserved];

    props.setLimits(limits);
  };
  
  const updateChannelLists = (descs: ChannelListDescriptor[]) => {
    if (!props.limits)
      return;
    
    let limits = copyProjectLimits(props.limits);
    limits.channel_lists = descs;
    
    props.setLimits(limits);
  };
  
  /*const addChannelList = (epg_id: string) => {
    console.log("addChannelList/epg_id=", epg_id);
    
    if (	!props.limits ||
        	epg_id == ""
       )
      return;
    
    const list = getChannelListByEpgId(epg_id, combinedLists);
    console.log("addChannelList/epg_id=", epg_id + "/list=", list);
    if (!list)
      return;
    
    const desc: ChannelListDescriptor = {
      id:				list.epg_id,
      start_from:		0,
    };
    
    let limits = copyProjectLimits(props.limits);
    limits.channel_lists.push(desc);
    
    props.setLimits(limits);
  };
  
  const deleteChannelList = (epg_id: string) => {
    console.log("deleteChannelList/epg_id=" + epg_id);
    
    if (	!props.limits ||
        	epg_id == ""
       )
      return;
    
    let limits = copyProjectLimits(props.limits);
    let i = 0;
    
    while (i < limits.channel_lists.length) {
      const desc = limits.channel_lists[i];
      if (desc.id != epg_id) {
        i++;
        continue;
      }
      
      limits.channel_lists.splice(i, 1);
    }
    console.log("deleteChannelList/epg_id=" + epg_id + "/limits.channel_lists=", limits.channel_lists);
    
    props.setLimits(limits);
  };
  
  const moveChannelList = (idx: number, move_by: number) => {
    if (!props.limits)
      return;
    
    let limits = copyProjectLimits(props.limits);
    const list = limits.channel_lists.splice(idx, 1);
    
    //if (move_by > 0)
    //  idx--;
    
    limits.channel_lists.splice(idx + move_by, 0, list[0]);
    
    props.setLimits(limits);
  };
  
  const handleselectionChange = (vals: "all" | Set<Key>, setter: (val: Set<Key>) => any, unsetter: (val: Set<Key>) => any) => {
    console.log("handleselectionChange/vals=", vals);
    
    if (vals === "all")
      return
    
    const val = vals.size
				?	[[...vals][0]]
				:	[];
	
    console.log("handleselectionChange/val=", val);
    setter(new Set(val));
    unsetter(new Set([]));
  };*/
  
  const limitsLists = props.limits && props.limits.channel_lists ? props.limits.channel_lists : [];
  
  const loading =	!isNew &&
			  		props.project.id != '' &&
			  		!props.limits;

	const adminOrPrivilage=(value:any)=>{
   return (role.admin || role?.privilages?.includes(value))
  }
  const privilageAccess=(value:any)=>{
    return role.admin || role?.privilages?.includes(value)
  }
  const aclsUrls = props.limits?.acls ?? [];
  const hasSeeAll = role?.privilages?.includes("SeeAllProjects");
  const seeAllOnly = hasSeeAll && !role.admin && !role?.privilages?.includes("EditProjectSettings");
  const managerNoEdit = !role.admin && !role?.privilages?.includes("EditProjectSettings") && !role?.privilages?.includes("CreateProjects");
  const seePartnerOnly =!role.admin && !role?.privilages?.includes("SetPartner");
  const seeChainmanagerOnly =!role.admin && !role?.privilages?.includes("SetChainmanager");
  const seeManagerOnly =!role.admin && !role?.privilages?.includes("SetManager");

  const hasAccess = (aclKey: string) => role?.admin || getField(aclsUrls, aclKey) == "1";

  const screentimeEnabled = role.admin || canOverrideScreentime(aclsUrls);

  // Deliberately the ACL alone, with no privilege term on top — the grant IS
  // the whole test, so an ACL-only Manager (who could not otherwise touch
  // project settings) still gets this button.
  const languageSelectorEnabled = hasAccess(languageSelectorAclKey);

  const setExecutive = ()=>{
    if(role.admin)
      return true
    const arr=["SetChainmanager","SetPartner","SetManager","SeeAllProjects"]
    return arr.some((item : any)=> role?.privilages?.includes(item));
  }

  const isProjectMember = React.useMemo(() => {
    const username = logins.login?.username;
    if (!username) return false;
    const localProjectId = logins.login?.local_project_id;
    if (localProjectId && localProjectId === props.project.id) return true;

    if (props.project.owner === username) return true;
    if (!props.limits) return false;
    const execs = props.limits.executives;
    return execs.partner?.includes(username) ||
           execs.chain_manager?.includes(username) ||
           execs.manager?.includes(username) ||
           // Task-80 §2.2 — the group's chain managers hold the role too.
           inheritedChainManagers.includes(username);
  }, [logins.login, props.project.id, props.project.owner, props.limits, inheritedChainManagers]);

  const canEdit = role.admin || role?.privilages?.includes("EditProjectSettings") || isProjectMember;

  // §4.3 — CreateProjects on top of the page's own edit gate (canEditProject
  // in Project.tsx); everyone else sees the value read-only.
  const canMoveGroup =	(role.admin || role?.privilages?.includes("CreateProjects")) &&
  						(role.admin || role?.privilages?.includes("EditProjectSettings") || (isNew && role?.privilages?.includes("CreateProjects")));

  // Same gate for the address and for both ends of its window.
  const publicIpReadOnly = seeAllOnly || !(canEdit || hasAccess("enable-public-ip"));

  return (
  <FlexRow direction="column"
           align="start"
           className="py-0 gap-[16px] max-w-[448px]">
    <CatHeader caption="projects:project:project_settings:general" />
    {(adminOrPrivilage("SeeAllProjects") || adminOrPrivilage("CreateProjects") || adminOrPrivilage("EditProjectSettings") || isProjectMember) &&
    <React.Fragment>
      <Input label={t('projects:project:project_settings:id') + '*'}
             isLoaded={!loading}
             isInvalid={props.idInvalid}
             isDisabled={!isNew}
             errorMessage={t('projects:project:project_settings:id_invalid')}
             value={props.project.id}
             onValueChange={updateId} />
      {!isNew &&
        <Input label={t('projects:project:project_settings:activation_code')}
               isLoaded={!loading}
               isReadOnly={seeAllOnly || managerNoEdit}
               value={props.limits ? props.limits.activation_code : ''}
               onValueChange={updateActivationCode} />
      }
      <Input label={t('projects:project:project_settings:name') + '*'}
             value={props.project.name}
             isInvalid={props.triedSave && props.nameInvalid}
             isLoaded={!loading}
             isReadOnly={seeAllOnly || managerNoEdit}
             errorMessage={t('projects:project:project_settings:name_invalid')}
             onValueChange={updateName} />
      <Dropdown label={t('projects:groups:group')}
                value={props.project.group ? props.project.group : noGroupKey}
                isLoaded={!loading}
                isDisabled={!canMoveGroup}
                options={groupOpts}
                setValue={canMoveGroup ? updateGroup : () => {}}>
        <>
        </>
      </Dropdown>
    </React.Fragment>
    }

  {(role.admin || !isNew) && (adminOrPrivilage("EditProjectSettings") || adminOrPrivilage("SeeAllProjects"))  &&
    <React.Fragment>
      <Input label={t('projects:project:project_settings:bexio_url')}
             value={bexioUrl}
             isLoaded={!loading}
             isReadOnly={!adminOrPrivilage("EditProjectSettings")}
             onValueChange={(val: string) => updateUrl("bexio_url", val)} />
      <TextArea value={notes}
                isLoaded={!loading}
                isReadOnly={!adminOrPrivilage("EditProjectSettings")}
                onValueChange={(val: string) => updateUrl("notes", val)}
                label={t("projects:project:project_settings:notes")}
                placeholder={t("projects:project:project_settings:notes_desc")} />
    </React.Fragment>
  }
  {/* { (adminOrPrivilage("CreateProjects") || adminOrPrivilage("SeeAllProjects")) && logins.logins &&
    <Dropdown label={t('projects:project:project_settings:owner') + '*'}
              value={props.project.owner}
              isLoaded={!loading}
              isDisabled={seeAllOnly}
              options={partnerOpts}
              isInvalid={props.triedSave && props.ownerInvalid}
              errorMessage={t('projects:project:project_settings:owner_invalid')}
              setValue={updateOwner}>
      <>
	  </>
    </Dropdown>
  } */}
  {/* { (role.admin || !isNew) && (adminOrPrivilage("CreateProjects") || adminOrPrivilage("SeeAllProjects") || adminOrPrivilage("EditProjectSettings"))&&
    <Input label={t('projects:project:project_settings:owner')}
           value={props.project.owner}
           isLoaded={!loading}
           isReadOnly={true} />
  } */}
  {!isNew &&
    <React.Fragment>
          {(adminOrPrivilage("EditProjectSettings") || adminOrPrivilage("SeeAllProjects")) &&
          <Input label={t('projects:project:project_settings:room_limit')}
            isLoaded={!loading}
            isReadOnly={seeAllOnly}
            value={props.limits && props.limits.rooms ? props.limits.rooms.toString() : ''}
            onValueChange={updateRooms} />
          }
          {(hasAccess("enable-public-ip") || adminOrPrivilage("EditProjectSettings") || adminOrPrivilage("SeeAllProjects")) &&
          <React.Fragment>
            {/* Address + its verdict pill; the pill carries the whole verdict,
                so there is no helper line under the box. */}
            <FlexRow align="center"
                     className="w-full gap-[12px]">
              <div className="flex-1 min-w-0">
                <Input label={t('projects:project:project_settings:public_ip')}
                       isLoaded={!loading}
                       isReadOnly={publicIpReadOnly}
                       isInvalid={publicIpKind === 'invalid'}
                       value={publicIp}
                       onValueChange={updatePublicIp} />
              </div>
            {!loading && publicIpKind !== 'empty' &&
              <span className={publicIpKind === 'invalid' ? ipPillBad : ipPillGood}>
                <span className="material-icons !text-[16px]">
                  { publicIpKind === 'invalid' ? 'error_outline' : 'check' }
                </span>
                { publicIpKind === 'invalid'
                    ? t('projects:project:project_settings:public_ip_status_invalid')
                    : t('projects:project:project_settings:public_ip_status_valid') }
              </span>
            }
            </FlexRow>

            {/* activates_at / expires_at on the same public_ip entry. Both
                optional: an empty end is unbounded on that side. */}
            <FlexRow direction="column"
                     align="start"
                     className="w-full gap-[10px]">
              <FlexRow align="center"
                       className="w-full gap-[12px]">
                <span className="text-[11px] font-bold uppercase tracking-wider text-[--Dark-Grey] whitespace-nowrap">
                  { t('projects:project:project_settings:public_ip_window') }
                </span>
                <div className="flex-1 h-[1px] bg-[#E6E9EF]" />
              </FlexRow>
              {/* min-w keeps the pair side by side in the 448px column and lets
                  it wrap instead of clipping the native date text if it ever
                  renders somewhere narrower. */}
              <FlexRow align="start"
                       className="w-full gap-[16px] flex-wrap">
                <div className="flex-1 min-w-[196px]">
                  <Input label={t('projects:project:project_settings:public_ip_from')}
                         type="datetime-local"
                         placeholder=" "
                         isLoaded={!loading}
                         isReadOnly={publicIpReadOnly}
                         value={expiryToLocalDateTime(publicIpActivatesAt)}
                         onValueChange={updatePublicIpActivatesAt} />
                </div>
                <div className="flex-1 min-w-[196px]">
                  <Input label={t('projects:project:project_settings:public_ip_to')}
                         type="datetime-local"
                         placeholder=" "
                         isLoaded={!loading}
                         isReadOnly={publicIpReadOnly}
                         isInvalid={publicIpWindowInvalid}
                         value={expiryToLocalDateTime(publicIpExpiresAt)}
                         onValueChange={updatePublicIpExpiresAt} />
                </div>
              </FlexRow>
            {publicIpWindowInvalid &&
              <span className="text-[12px] text-red-500">
                { t('common:urls:activation_after_expiry') }
              </span>
            }
            </FlexRow>
          </React.Fragment>
          }
          {(hasAccess("enable-language") || adminOrPrivilage("SeeAllProjects")) &&
            <Dropdown label={t('projects:project:project_settings:categories:language')}
              value={props?.project?.languages?.[0]?.code || 'default'}
              placeholder={t('projects:project:project_settings:categories:select_the_language')}
              isDisabled={!(canEdit || hasAccess("enable-language"))}
              options={languageOpts}
              setValue={updateLanguage}>
              <>
              </>
            </Dropdown>
          }
          {
            setExecutive() &&
            <CatHeader caption="projects:project:project_settings:executives" />
          }
          {(adminOrPrivilage("SetPartner") || adminOrPrivilage("SeeAllProjects")) &&
            <Dropdown label={t('projects:project:project_settings:partner:caption') + '*'}
              value={props.limits ? props.limits.executives.partner : []}
              placeholder={t('projects:project:project_settings:partner:placeholder')}
              selectionMode="multiple"
              disallowEmptySelection={false}
              isDisabled={seePartnerOnly}
              options={execOpts}
              isSearchable
              searchPlaceholder={t('common:search')}
              disabledKeys={mwLightNames}
              //isInvalid={props.triedSave && props.partnerInvalid}
              errorMessage={t('projects:project:project_settings:partner:invalid')}
              setValue={role.admin || role?.privilages?.includes("SetPartner") ? updatePartner : () => {}}>
              <>
              </>
            </Dropdown>
          }
          {
            (adminOrPrivilage("SetChainmanager") || adminOrPrivilage("SeeAllProjects")) &&
            <Dropdown label={t('projects:project:project_settings:chain_manager:caption') + '*'}
              value={props.limits ? props.limits.executives.chain_manager : []}
              placeholder={t('projects:project:project_settings:chain_manager:placeholder')}
              selectionMode="multiple"
              disallowEmptySelection={false}
              isDisabled={seeChainmanagerOnly}
              options={execOpts}
              isSearchable
              searchPlaceholder={t('common:search')}
              disabledKeys={mwLightNames}
              errorMessage={t('projects:project:project_settings:chain_manager:invalid')}
              setValue={role.admin || role?.privilages?.includes("SetChainmanager") ? updateChainManager : () => {}}>
              <>
              </>
            </Dropdown>
          }
          {/* Task-80 §4.4 — what the group grants, on its own read-only line.
              Never merged into the dropdown above: that one is the project's
              own list and is what the limits.json save sends. */}
          {(adminOrPrivilage("SetChainmanager") || adminOrPrivilage("SeeAllProjects")) && storedGroupId &&
            <FlexRow direction="column"
                     align="start"
                     className="w-full gap-[2px] -mt-[8px]">
              <span className="text-[12px] text-[--Dark-Grey]">
                { t('projects:groups:granted_by_group') }
              </span>
              <span className="text-[14px]">
                <span className={adminOrPrivilage("CreateProjects") ? "text-[#1B69AA] underline cursor-pointer" : ""}
                      onClick={() => {
                        if (adminOrPrivilage("CreateProjects"))
                          navigate('/projects?group=' + encodeURIComponent(storedGroupId) + '&edit=1');
                      }}>
                  { inheritedGroup ? inheritedGroup.name : storedGroupId }
                </span>
                { ': ' + (inheritedChainManagers.length > 0 ? inheritedChainManagers.join(", ") : '-') }
              </span>
            </FlexRow>
          }
          {
            (adminOrPrivilage("SetManager") || adminOrPrivilage("SeeAllProjects")) &&
            <Dropdown label={t('projects:project:project_settings:manager:caption') + '*'}
              value={props.limits ? props.limits.executives.manager : []}
              placeholder={t('projects:project:project_settings:manager:placeholder')}
              selectionMode="multiple"
              isDisabled={seeManagerOnly}
              options={execOpts}
              isSearchable
              searchPlaceholder={t('common:search')}
              disabledKeys={mwLightNames}
              disallowEmptySelection={false}
              //isInvalid={props.triedSave && props.managerInvalid}
              errorMessage={t('projects:project:project_settings:manager:invalid')}
              setValue={role.admin || role?.privilages?.includes("SetManager") ? updateManager : () => {}}>
              <>
              </>
            </Dropdown>
          }
          {/* MW light — the fourth list, after Manager (§2.1). Only an admin
              may change it; everyone else who can see the executives at all
              sees the stored names as read-only text. A listed name that no
              longer exists or is no longer eligible is SHOWN, marked, and
              removable — never silently dropped. */}
          {
            role.admin &&
            <React.Fragment>
              {/* §3.2 (rev 2) — the Checkout switch in System settings never
                  touches this list: MW light stays as it is while it is off. */}
              <Dropdown label={t('projects:project:project_settings:mw_light:caption')}
                value={mwLightNames}
                placeholder={t('projects:project:project_settings:mw_light:placeholder')}
                selectionMode="multiple"
                disallowEmptySelection={false}
                isLoaded={!loading}
                options={mwLightOpts}
                isSearchable
                searchPlaceholder={t('common:search')}
                setValue={updateMwLight}>
                <>
                </>
              </Dropdown>
              <div className="r12 text-[--Dark-Grey] max-w-[448px]">
                { t('projects:project:project_settings:mw_light:help') }
              </div>
            {mwLightOverlapList.length > 0 &&
              <div className="r12 text-[--Warning] max-w-[448px]">
              {mwLightOverlapList.map((o, i) =>
                <div key={i}>
                  { t('projects:project:project_settings:mw_light:overlap', {
                      user: o.username,
                      list: o.list,
                    }) }
                </div>
              )}
              </div>
            }
            {mwLightNoActive &&
              <div className="r12 text-[--Warning] max-w-[448px]">
                { noActiveMwLightUserWarning }
              </div>
            }
              {/* The hotel's Checkout (#91 §0.8 / §3.1a) — admins only. The
                  pill says what applies to this hotel, the switch picks
                  between following System settings and overriding it, and the
                  change is saved with the rest of the limits. Hidden until
                  /master.json answered, so it never acts on a guess. */}
            {!master.loading && props.limits &&
              <SettingsCard icon="logout"
                            title={t('projects:project:project_settings:checkout:caption')}
                            subtitle={t('projects:project:project_settings:checkout:card_subtitle')}
                            pill={
                              <StatusPill tone={checkoutEffective ? "green" : "grey"}>
                                { checkoutEffective
                                  ?	t('projects:project:project_settings:checkout:pill_enabled')
                                  :	t('projects:project:project_settings:checkout:pill_disabled')
                                }
                              </StatusPill>
                            }>
                <SegmentedSwitch
                  options={[
                    {
                      label:	t('projects:project:project_settings:checkout:seg_follow'),
                      sub:		checkoutSwitchedOn(master)
                      			?	t('projects:project:project_settings:checkout:seg_follow_sub_on')
                      			:	t('projects:project:project_settings:checkout:seg_follow_sub_off'),
                    },
                    {
                      label:	checkoutSwitchedOn(master)
                      			?	t('projects:project:project_settings:checkout:seg_disable')
                      			:	t('projects:project:project_settings:checkout:seg_enable'),
                      sub:		t('projects:project:project_settings:checkout:seg_override_sub'),
                    },
                  ]}
                  selected={hotelCheckout.differs ? 1 : 0}
                  onSelect={(i) => {
                    if (i !== (hotelCheckout.differs ? 1 : 0))
                      pressHotelCheckout();
                  }} />
                <Hint>
                  { t('projects:project:project_settings:checkout:help') }
                </Hint>
              </SettingsCard>
            }
              {/* Project expiration (#93 §2.1) — admins only. Saved with the
                  rest of the limits; the keys are sent only once touched. */}
            {props.limits &&
              <SettingsCard icon="event"
                            title={t('projects:project:project_settings:expiration:caption')}
                            subtitle={t('projects:project:project_settings:expiration:subtitle')}
                            pill={
                              expiresAt === 0
                              ?	<StatusPill tone="grey">
                                  { t('projects:project:project_settings:expiration:pill_none') }
                                </StatusPill>
                              :	expiryExpired
                              ?	<StatusPill tone="red">
                                  { t('projects:project:project_settings:expiration:pill_expired') }
                                </StatusPill>
                              :	expiryPastWarning
                              ?	<StatusPill tone="amber">
                                  { t('projects:project:project_settings:expiration:pill_unsaved') }
                                </StatusPill>
                              :	<StatusPill tone="blue">
                                  { t('projects:project:project_settings:expiration:pill_future', {
                                      countdown: getExpiryCountdown(expiresAt) || "0m",
                                    }) }
                                </StatusPill>
                            }>
                <FlexRow align="center"
                         className="w-full gap-[12px]">
                  <div className="flex-1 min-w-0">
                    <Input label={t('projects:project:project_settings:expiration:expires_on')}
                           type="datetime-local"
                           placeholder=" "
                           isLoaded={!loading}
                           value={expiryToLocalDateTime(expiresAt)}
                           onValueChange={(val: string) => updateExpiresAt(localDateTimeToExpiry(val))} />
                  </div>
                  <Button layout="white"
                          icon="close"
                          width="min-w-[120px]"
                          caption={t('projects:project:project_settings:expiration:no_expiration')}
                          isDisabled={expiresAt === 0}
                          onPress={() => updateExpiresAt(0)} />
                </FlexRow>
                <FlexRow align="center"
                         className="gap-[8px] flex-wrap">
                {expiryPresets().map((preset) =>
                  <button key={preset.key}
                          type="button"
                          className="rounded-full border-solid border-1 border-[--Lines] bg-white px-[12px] py-[4px] text-[12px] font-medium leading-[16px] text-black hover:border-[--Primetime-main] hover:text-[--Primetime-main]"
                          onClick={() => updateExpiresAt(preset.ts)}>
                    { t(`projects:project:project_settings:expiration:${preset.key}`) }
                  </button>
                )}
                </FlexRow>
              {expiresAt > 0 && !expiryPastWarning &&
                <div className={"r12 " + (expiryExpired ? "text-red-500" : "text-[--Dark-Grey]")}>
                  {	expiryExpired
                  	?	t('projects:project:project_settings:expiration:status_expired', {
                  	      date: formatExpiryDate(expiresAt),
                  	    })
                  	:	t('projects:project:project_settings:expiration:status_future', {
                  	      date: formatExpiryDate(expiresAt),
                  	      countdown: getExpiryCountdown(expiresAt) || "0m",
                  	    })
                  }
                </div>
              }
              {expiryPastWarning &&
                <div className="r12 text-[--Warning]">
                  { t('projects:project:project_settings:expiration:warning_past') }
                </div>
              }
                <div className="w-full">
                  <TextArea value={expiredMessage}
                            isLoaded={!loading}
                            showChars={false}
                            isInvalid={expiredMessage.length > 500}
                            onValueChange={updateExpiredMessage}
                            label={t('projects:project:project_settings:expiration:message')}
                            placeholder={t('projects:project:project_settings:expiration:message_placeholder')} />
                  <FlexRow align="center"
                           className="w-full justify-between gap-[12px] pt-[4px]">
                    <Hint>
                      { t('projects:project:project_settings:expiration:message_help') }
                    </Hint>
                    <span className={(expiredMessage.length > 500 ? "text-red-500" : "text-[--Dark-Grey]") + " text-[12px] leading-[16px] whitespace-nowrap"}>
                      { expiredMessage.length + " / 500" }
                    </span>
                  </FlexRow>
                </div>
              </SettingsCard>
            }
            </React.Fragment>
          }
          {
            !role.admin &&
            setExecutive() &&
            <FlexRow direction="column"
                     align="start"
                     className="w-full max-w-[448px] gap-[4px]">
              <div className="r10 text-[--Dark-Grey]">
                { t('projects:project:project_settings:mw_light:caption') }
              </div>
              <div className="r14 !text-black break-words">
                {	mwLightNames.length
                	?	mwLightNames.join(", ")
                	:	"-"
                }
              </div>
            </FlexRow>
          }
          {
            (role?.admin || adminOrPrivilage("SeeAllProjects")) &&
            <>
              <CatHeader caption="projects:project:project_settings:categories:caption" />
              <Dropdown label={t('projects:project:project_settings:categories:tags')}
                value={props?.project?.tags ? props.project.tags.map((t) => t.tag || '') : []}
                placeholder={t('projects:project:project_settings:categories:select_the_tag')}
                selectionMode="multiple"
                disallowEmptySelection={false}
                isDisabled={seeAllOnly}
                options={tagOpts}
                errorMessage={t('projects:project:project_settings:partner:invalid')}
                setValue={updateTags}>
                <>
                </>
              </Dropdown>
            </>
          }
          {
            (role?.admin || adminOrPrivilage("SeeAllProjects")) &&
            <>
              <CatHeader caption="projects:project:project_settings:firmware:caption" />
              <FirmwaresEditor
                firmwares={props.limits?.overrides?.firmware || []}
                setFirmwares={handleFirmwareChange}
                triedSave={props.triedSave}
              />
            </>
          }
      
	{combinedLists.lists &&
	  <ProjectLists lists={limitsLists}
                    setLists={(role.admin || role?.privilages?.includes("SetChannelList")) ? updateChannelLists : () => {}}
                    sources={combinedLists} />
	}
	
	{/* Screen time only appears once the `enable-screentime` Manager ACL is on
	    (or a legacy per-field key still stands in for it). The ACL is the only
	    gate — no privilege on top — so a Manager of this project edits the
	    project defaults as soon as an admin turns it on. */}
	{screentimeEnabled &&
	  <ScreenTimeSettings limits={props.limits}
	                      setLimits={props.setLimits}
	                      isLoaded={!loading}
	                      isReadOnly={seeAllOnly || !canEdit} />
	}

	{/* Channellist language selector — same ACL-alone gate as screen time
	    above, and same inline layout as the room-level section: no button/
	    panel, the editor sits directly on the page. The project's own Save
	    button is what persists it (see LanguageSelectorSettings). */}
	{/* No CatHeader here any more: the card titles itself (globe, "Language
	    choices", and a pill saying whether the guest currently sees it), so a
	    heading above it would say the same thing twice. */}
	{languageSelectorEnabled && props.limits &&
	  <FlexRow direction="column"
	           align="start"
	           className="pt-[8px] w-full">
	    <LanguageSelectorSettings config={props.limits.language_selector}
	                              setConfig={updateLanguageSelector}
	                              lists={combinedLists}
	                              isLoaded={!loading}
	                              isReadOnly={seeAllOnly || !(canEdit || languageSelectorEnabled)} />
	  </FlexRow>
	}

	{(adminOrPrivilage("EditProjectSettings") || adminOrPrivilage("SeeAllProjects")) &&
	  <React.Fragment>
        <Dropdown label={t('projects:project:project_settings:overrides:tracks')}
		          value={tracksOverride}
		          isLoaded={!loading}
		          isDisabled={seeAllOnly}
		          options={trackFormatOpts}
		          setValue={updateTracksOverride}>
		  <>
		  </>
	    </Dropdown>
        <CatHeader caption="common:urls:caption"
	               margin="mt-[8px]" />
	    <UrlsEditor urls={props.limits && props.limits.urls
	                        ? props.limits.urls.filter((u) => u.type !== "public_ip")
	                        : []}
				    setUrls={updateLimitsUrls}
				    triedSave={props.triedSave}
				    setUrlInvalid={setGenericUrlsInvalid} />
	  </React.Fragment>
    }
    </React.Fragment>
  }
  </FlexRow>
  );
}

export default ProjectSettings;


